import { computed, ref, watch, type Ref } from 'vue';
import type { ExternalJobMarketJob } from '@/types/externalJobMarket';

export interface EjmFilters {
  search: string;
  status: '' | 'active' | 'expired';
  languages: string[];
  location: string;
  freshness: '' | 'today' | '7days' | '30days';
  company: string;
  properties: Record<string, string[]>;
  applicationStatus: '' | 'no_applications' | 'below_target' | 'on_target' | 'above_target';
  hasHires: '' | 'yes' | 'no';
}

export interface PropertyDefinition {
  key: string;
  label: string;
  type: 'select' | 'boolean';
  values: { value: string; label: string; count: number }[];
}

const MAX_FILTER_VALUES = 80;
const MIN_FILTER_VALUES = 2;
const IGNORED_PROPERTY_PATTERNS = [
  /^ohws_platform_id$/,
  /^ohws_posting_id$/,
  /^ohws_viewkey$/,
  /reference_code/,
  /^sza_/,
];

function extractPropertyValues(propValue: any): string[] {
  if (propValue == null) return [];
  if (typeof propValue === 'object' && !Array.isArray(propValue)) {
    return Object.values(propValue).map(String);
  }
  return [String(propValue)];
}

function formatPropertyLabel(key: string): string {
  return key
    .replace(/^ohws_attr_/, '')
    .replace(/^ohws_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function createDefaultFilters(): EjmFilters {
  return {
    search: '',
    status: '',
    languages: [],
    location: '',
    freshness: '',
    company: '',
    properties: {},
    applicationStatus: '',
    hasHires: '',
  };
}

export function useEjmFilters(allJobs: Ref<ExternalJobMarketJob[]>) {
  const filters = ref<EjmFilters>(createDefaultFilters());
  const currentPage = ref(1);
  const pageSize = ref(20);

  /* -------------------------------- COMPUTED -------------------------------- */
  const availableLanguages = computed(() => {
    const langs = new Set<string>();
    for (const job of allJobs.value) {
      if (job.language) langs.add(job.language);
    }
    return Array.from(langs).sort();
  });

  const availableCompanies = computed(() => {
    const companies = new Set<string>();
    for (const job of allJobs.value) {
      if (job.company_name) companies.add(job.company_name);
    }
    return Array.from(companies).sort();
  });

  const availableProperties = computed<PropertyDefinition[]>(() => {
    const propMap = new Map<string, Map<string, number>>();

    for (const job of allJobs.value) {
      if (!job.properties) continue;
      for (const [key, value] of Object.entries(job.properties)) {
        if (IGNORED_PROPERTY_PATTERNS.some((p) => p.test(key))) continue;

        if (!propMap.has(key)) propMap.set(key, new Map());
        const valueMap = propMap.get(key)!;

        for (const v of extractPropertyValues(value)) {
          valueMap.set(v, (valueMap.get(v) || 0) + 1);
        }
      }
    }

    const definitions: PropertyDefinition[] = [];

    for (const [key, valueMap] of propMap) {
      if (valueMap.size < MIN_FILTER_VALUES || valueMap.size > MAX_FILTER_VALUES) continue;

      const isBool = valueMap.size <= 2 && [...valueMap.keys()].every((v) => v === 'true' || v === 'false');
      const values = [...valueMap.entries()]
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count);

      definitions.push({
        key,
        label: formatPropertyLabel(key),
        type: isBool ? 'boolean' : 'select',
        values,
      });
    }

    return definitions.sort((a, b) => a.label.localeCompare(b.label));
  });

  const filteredJobs = computed(() => {
    let jobs = allJobs.value;
    const f = filters.value;

    if (f.search) {
      const q = f.search.toLowerCase();
      jobs = jobs.filter(
        (job) =>
          job.title?.toLowerCase().includes(q) ||
          job.company_name?.toLowerCase().includes(q) ||
          job.workplace_location?.toLowerCase().includes(q) ||
          job.workplace_city?.toLowerCase().includes(q) ||
          job.provider_key?.toLowerCase().includes(q)
      );
    }

    if (f.status === 'active') {
      const now = Date.now();
      jobs = jobs.filter((job) => job.active && (!job.expires || new Date(job.expires).getTime() > now));
    } else if (f.status === 'expired') {
      const now = Date.now();
      jobs = jobs.filter((job) => !job.active || (!!job.expires && new Date(job.expires).getTime() <= now));
    }

    if (f.languages.length > 0) {
      jobs = jobs.filter((job) => f.languages.includes(job.language));
    }

    if (f.location) {
      const loc = f.location.toLowerCase();
      jobs = jobs.filter(
        (job) => job.workplace_location?.toLowerCase().includes(loc) || job.workplace_city?.toLowerCase().includes(loc)
      );
    }

    if (f.company) {
      const comp = f.company.toLowerCase();
      jobs = jobs.filter((job) => job.company_name?.toLowerCase().includes(comp));
    }

    if (f.freshness) {
      const now = new Date();
      let cutoff: Date;
      switch (f.freshness) {
        case 'today':
          cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7days':
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
      }
      jobs = jobs.filter((job) => new Date(job.created_at) >= cutoff);
    }

    if (f.applicationStatus) {
      jobs = jobs.filter((job) => {
        const count = job.application_count ?? 0;
        const target = job.target_application_count ?? 1;
        const ratio = count / target;
        switch (f.applicationStatus) {
          case 'no_applications':
            return count === 0;
          case 'below_target':
            return ratio < 0.5;
          case 'on_target':
            return ratio >= 0.5 && ratio < 1;
          case 'above_target':
            return ratio >= 1;
          default:
            return true;
        }
      });
    }

    if (f.hasHires === 'yes') {
      jobs = jobs.filter((job) => (job.hire_count ?? 0) > 0);
    } else if (f.hasHires === 'no') {
      jobs = jobs.filter((job) => (job.hire_count ?? 0) === 0);
    }

    const activePropertyFilters = Object.entries(f.properties).filter(([, vals]) => vals.length > 0);
    if (activePropertyFilters.length > 0) {
      jobs = jobs.filter((job) => {
        return activePropertyFilters.every(([key, selectedValues]) => {
          const propValue = job.properties?.[key];
          if (propValue == null) return false;
          const jobValues = extractPropertyValues(propValue);
          return selectedValues.some((sv) => jobValues.includes(sv));
        });
      });
    }

    return jobs;
  });

  const filterCount = computed(() => filteredJobs.value.length);
  const pageCount = computed(() => Math.max(1, Math.ceil(filterCount.value / pageSize.value)));

  const paginatedJobs = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value;
    return filteredJobs.value.slice(start, start + pageSize.value);
  });

  const startRecordIndex = computed(() => {
    if (filterCount.value === 0) return 0;
    return (currentPage.value - 1) * pageSize.value + 1;
  });

  const endRecordIndex = computed(() => {
    return Math.min(currentPage.value * pageSize.value, filterCount.value);
  });

  const hasActiveFilters = computed(() => {
    const f = filters.value;
    return (
      f.status !== '' ||
      f.languages.length > 0 ||
      f.location !== '' ||
      f.freshness !== '' ||
      f.company !== '' ||
      f.applicationStatus !== '' ||
      f.hasHires !== '' ||
      Object.values(f.properties).some((vals) => vals.length > 0)
    );
  });

  const activeFilterCount = computed(() => {
    let count = 0;
    const f = filters.value;
    if (f.status) count++;
    if (f.languages.length) count++;
    if (f.location) count++;
    if (f.freshness) count++;
    if (f.company) count++;
    if (f.applicationStatus) count++;
    if (f.hasHires) count++;
    count += Object.values(f.properties).filter((vals) => vals.length > 0).length;
    return count;
  });

  watch(
    () => [
      filters.value.search,
      filters.value.status,
      filters.value.languages,
      filters.value.location,
      filters.value.freshness,
      filters.value.company,
      filters.value.applicationStatus,
      filters.value.hasHires,
      filters.value.properties,
    ],
    () => {
      currentPage.value = 1;
    },
    { deep: true } // deep needed for properties object
  );

  /* --------------------------------- METHODS -------------------------------- */
  const resetFilters = () => {
    Object.assign(filters.value, createDefaultFilters());
  };

  const changePage = (page: number) => {
    currentPage.value = Math.max(1, Math.min(page, pageCount.value));
  };

  const togglePropertyValue = (key: string, value: string) => {
    if (!filters.value.properties[key]) {
      filters.value.properties[key] = [value];
      return;
    }
    const idx = filters.value.properties[key].indexOf(value);
    if (idx === -1) {
      filters.value.properties[key].push(value);
    } else {
      filters.value.properties[key].splice(idx, 1);
    }
  };

  const clearPropertyFilter = (key: string) => {
    filters.value.properties[key] = [];
  };

  return {
    filters,
    currentPage,
    pageSize,
    filteredJobs,
    paginatedJobs,
    filterCount,
    pageCount,
    startRecordIndex,
    endRecordIndex,
    availableLanguages,
    availableCompanies,
    availableProperties,
    hasActiveFilters,
    activeFilterCount,
    resetFilters,
    changePage,
    togglePropertyValue,
    clearPropertyFilter,
  };
}
